import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Mail, Phone, Trash2, AlertTriangle, XCircle, Eye, EyeOff, Wand2, Loader2 } from 'lucide-react';
import { RichTextEditor } from './RichTextEditor';
import { WhatsAppPreview } from './WhatsAppPreview';
import { EmailPreview } from './EmailPreview';
import { SMSPreview } from './SMSPreview';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';

type ChannelType = 'whatsapp' | 'email' | 'sms' | 'protesto' | 'negativacao';

interface CollectionStep {
  id: string;
  step_number: number;
  trigger_days: number;
  channel: ChannelType;
  is_active: boolean | null;
  template_id?: string | null;
  collection_rule_id?: string;
  company_id?: string;
  content?: string;
  subject?: string;
  timing_mode?: 'relative' | 'in_order';
  auto_hour?: boolean;
  send_hour?: string;
}

interface CobrancaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ruleId: string;
  step: CollectionStep | null;
  onSaved: () => void;
}

const channels = [
  { id: 'whatsapp' as const, label: 'WhatsApp', icon: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
      <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a7.963 7.963 0 01-4.066-1.112l-2.827.84.84-2.827A7.963 7.963 0 014 12c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"/>
    </svg>
  ), color: 'accent', hasContent: true },
  { id: 'sms' as const, label: 'SMS', icon: Phone, color: 'careful', hasContent: true },
  { id: 'email' as const, label: 'E-mail', icon: Mail, color: 'basic', hasContent: true },
  { id: 'protesto' as const, label: 'Protesto', icon: AlertTriangle, color: 'destructive', hasContent: false },
  { id: 'negativacao' as const, label: 'Negativação', icon: XCircle, color: 'destructive', hasContent: false },
] as const;

const placeholders = [
  { key: '{nome}', desc: 'Nome do cliente' },
  { key: '{valor}', desc: 'Valor da fatura' },
  { key: '{vencimento}', desc: 'Data de vencimento' },
  { key: '{link_pagamento}', desc: 'Link para pagamento' },
];

const timingModes = [
  { id: 'before', label: 'Dias antes' },
  { id: 'after', label: 'Dias depois' },
  { id: 'in_order', label: 'Na ordem' },
] as const;

// Interface para conteúdo separado por canal
interface ChannelContent {
  whatsapp: string;
  sms: string;
  email: { subject: string; content: string };
}

const defaultChannelContent: ChannelContent = {
  whatsapp: '',
  sms: '',
  email: { subject: '', content: '' }
};

export function CobrancaModal({ open, onOpenChange, ruleId, step, onSaved }: CobrancaModalProps) {
  const [formData, setFormData] = useState({
    channel: 'whatsapp' as ChannelType,
    trigger_days: 0,
    timing_mode: 'after' as 'before' | 'after' | 'in_order',
    hour: '09:00',
    auto_hour: true,
  });
  
  // Estado separado para conteúdo de cada canal
  const [channelContent, setChannelContent] = useState<ChannelContent>(defaultChannelContent);
  
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const queryClient = useQueryClient();

  // Helpers para obter/definir conteúdo do canal atual
  const getCurrentContent = () => {
    if (formData.channel === 'email') {
      return channelContent.email.content;
    } else if (formData.channel === 'whatsapp') {
      return channelContent.whatsapp;
    } else if (formData.channel === 'sms') {
      return channelContent.sms;
    }
    return '';
  };

  const getCurrentSubject = () => {
    if (formData.channel === 'email') {
      return channelContent.email.subject;
    }
    return '';
  };

  const setCurrentContent = (content: string) => {
    if (formData.channel === 'email') {
      setChannelContent(prev => ({
        ...prev,
        email: { ...prev.email, content }
      }));
    } else if (formData.channel === 'whatsapp') {
      setChannelContent(prev => ({ ...prev, whatsapp: content }));
    } else if (formData.channel === 'sms') {
      setChannelContent(prev => ({ ...prev, sms: content }));
    }
  };

  const setCurrentSubject = (subject: string) => {
    if (formData.channel === 'email') {
      setChannelContent(prev => ({
        ...prev,
        email: { ...prev.email, subject }
      }));
    }
  };

  useEffect(() => {
    if (open) {
      if (step) {
        const isBefore = step.trigger_days < 0;
        const isInOrder = step.timing_mode === 'in_order';
        setFormData({
          channel: step.channel,
          trigger_days: Math.abs(step.trigger_days),
          timing_mode: isInOrder ? 'in_order' : (isBefore ? 'before' : 'after'),
          hour: step.send_hour || '09:00',
          auto_hour: step.auto_hour ?? true,
        });
        
        // Carregar conteúdo no canal correto
        const newContent = { ...defaultChannelContent };
        if (step.channel === 'email') {
          newContent.email = { 
            subject: step.subject || '', 
            content: step.content || '' 
          };
        } else if (step.channel === 'whatsapp') {
          newContent.whatsapp = step.content || '';
        } else if (step.channel === 'sms') {
          newContent.sms = step.content || '';
        }
        setChannelContent(newContent);
      } else {
        setFormData({
          channel: 'whatsapp',
          trigger_days: 0,
          timing_mode: 'after',
          hour: '09:00',
          auto_hour: true,
        });
        setChannelContent(defaultChannelContent);
      }
      setError('');
      setShowPreview(false);
    }
  }, [open, step]);

  const mutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('Empresa não encontrada');

      let triggerDays = formData.trigger_days;
      if (formData.timing_mode === 'before') {
        triggerDays = -Math.abs(formData.trigger_days);
      } else if (formData.timing_mode === 'in_order') {
        triggerDays = 999; // Placeholder for in_order
      }

      // Get max step number
      const { data: existingSteps } = await supabase
        .from('collection_steps')
        .select('step_number')
        .eq('collection_rule_id', ruleId)
        .order('step_number', { ascending: false })
        .limit(1);

      const nextStepNumber = existingSteps?.[0]?.step_number 
        ? existingSteps[0].step_number + 1 
        : 1;

      // Obter conteúdo do canal atual
      let content = '';
      let subject = '';
      if (formData.channel === 'email') {
        content = channelContent.email.content;
        subject = channelContent.email.subject;
      } else if (formData.channel === 'whatsapp') {
        content = channelContent.whatsapp;
      } else if (formData.channel === 'sms') {
        content = channelContent.sms;
      }

      const payload = {
        channel: formData.channel,
        trigger_days: triggerDays,
        collection_rule_id: ruleId,
        company_id: profile.company_id,
        step_number: step?.step_number || nextStepNumber,
        is_active: true,
        content: content || null,
        subject: subject || null,
        timing_mode: formData.timing_mode === 'in_order' ? 'in_order' : 'relative',
        auto_hour: formData.auto_hour,
        send_hour: formData.auto_hour ? null : formData.hour
      };

      if (step) {
        const { error } = await supabase
          .from('collection_steps')
          .update(payload)
          .eq('id', step.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('collection_steps')
          .insert(payload);
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-steps', ruleId] });
      onSaved();
      onOpenChange(false);
    },
    onError: (err: Error) => {
      setError(err.message);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!step) return;
      const { error } = await supabase
        .from('collection_steps')
        .delete()
        .eq('id', step.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-steps', ruleId] });
      onOpenChange(false);
    }
  });

  const insertPlaceholder = (placeholder: string) => {
    setCurrentContent(getCurrentContent() + placeholder);
  };

  const enhanceWithAI = async () => {
    setIsEnhancing(true);
    toast.info('Consultando IA...');

    // Simulate AI processing
    await new Promise(resolve => setTimeout(resolve, 1500));

    const enhancedTexts: Record<string, { content: string; subject?: string }> = {
      whatsapp: {
        content: `Olá {nome}! 👋

Identificamos uma pendência no valor de {valor}, com vencimento em {vencimento}.

Para sua comodidade, disponibilizamos um link exclusivo para pagamento:
🔗 {link_pagamento}

Estamos à disposição para ajudar!
Equipe de Cobrança`
      },
      email: {
        subject: 'Importante: Regularize sua pendência - {valor}',
        content: `<p style="margin-bottom: 16px;">Prezado(a) <strong>{nome}</strong>,</p>

<p style="margin-bottom: 16px;">Esperamos que esta mensagem o(a) encontre bem.</p>

<p style="margin-bottom: 16px;">Identificamos em nossos registros uma pendência financeira no valor de <strong>{valor}</strong>, com vencimento previsto para <strong>{vencimento}</strong>.</p>

<p style="margin-bottom: 16px;">Para facilitar a regularização, disponibilizamos um link seguro para pagamento:</p>

<p style="margin-bottom: 16px;"><a href="{link_pagamento}" style="color: #2563eb; text-decoration: underline;">{link_pagamento}</a></p>

<p style="margin-bottom: 16px;">Caso já tenha efetuado o pagamento, por favor desconsidere este aviso.</p>

<p style="margin-bottom: 16px;">Estamos à disposição para quaisquer esclarecimentos.</p>

<p style="margin-bottom: 0;">Atenciosamente,<br/><strong>Equipe de Cobrança</strong></p>`
      },
      sms: {
        content: `{nome}, sua fatura de {valor} vence em {vencimento}. Pague pelo link: {link_pagamento}`
      }
    };

    const enhanced = enhancedTexts[formData.channel] || enhancedTexts.whatsapp;
    
    // Atualizar conteúdo no canal correto
    if (formData.channel === 'email') {
      setChannelContent(prev => ({
        ...prev,
        email: {
          subject: enhanced.subject || prev.email.subject,
          content: enhanced.content
        }
      }));
    } else if (formData.channel === 'whatsapp') {
      setChannelContent(prev => ({ ...prev, whatsapp: enhanced.content }));
    } else if (formData.channel === 'sms') {
      setChannelContent(prev => ({ ...prev, sms: enhanced.content }));
    }

    setIsEnhancing(false);
    toast.success('Texto aprimorado!');
  };

  const currentChannel = channels.find(c => c.id === formData.channel);
  const showContentEditor = currentChannel?.hasContent;

  const getChannelColorClasses = (channelId: string, isSelected: boolean) => {
    const colorMap: Record<string, { selected: string; unselected: string }> = {
      whatsapp: {
        selected: 'border-[#25d366] bg-[#25d366]/10 text-[#25d366]',
        unselected: 'border-border hover:border-[#25d366]/50 text-muted-foreground'
      },
      sms: {
        selected: 'border-[hsl(var(--careful-500))] bg-[hsl(var(--careful-50))] text-[hsl(var(--careful-600))]',
        unselected: 'border-border hover:border-[hsl(var(--careful-300))] text-muted-foreground'
      },
      email: {
        selected: 'border-[hsl(var(--basic-500))] bg-[hsl(var(--basic-50))] text-[hsl(var(--basic-600))]',
        unselected: 'border-border hover:border-[hsl(var(--basic-300))] text-muted-foreground'
      },
      protesto: {
        selected: 'border-destructive bg-destructive/10 text-destructive',
        unselected: 'border-border hover:border-destructive/50 text-muted-foreground'
      },
      negativacao: {
        selected: 'border-destructive bg-destructive/10 text-destructive',
        unselected: 'border-border hover:border-destructive/50 text-muted-foreground'
      }
    };
    return isSelected ? colorMap[channelId]?.selected : colorMap[channelId]?.unselected;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step ? 'Editar Comunicação' : 'Adicionar comunicação'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 pt-4">
          {/* Channel Selection */}
          <div>
            <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
              Canal de Comunicação
            </Label>
            <div className="grid grid-cols-5 gap-2 mt-2">
              {channels.map((ch) => {
                const Icon = ch.icon;
                const isSelected = formData.channel === ch.id;
                return (
                  <button
                    key={ch.id}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, channel: ch.id }))}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all hover-pop ${getChannelColorClasses(ch.id, isSelected)}`}
                  >
                    <Icon className="size-5" />
                    <span className="text-xs font-medium uppercase">
                      {ch.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Timing - Aligned layout */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider block">
                Dia de Envio
              </Label>
              <p className="text-sm text-muted-foreground italic mb-3">
                Em relação à data de vencimento.
              </p>
              <div className="flex gap-2 items-center">
                {formData.timing_mode !== 'in_order' && (
                  <Input
                    type="number"
                    min="0"
                    value={formData.trigger_days}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      trigger_days: parseInt(e.target.value) || 0 
                    }))}
                    className="w-16 h-10"
                  />
                )}
                <select
                  value={formData.timing_mode}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    timing_mode: e.target.value as 'before' | 'after' | 'in_order'
                  }))}
                  className="h-10 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  {timingModes.map((mode) => (
                    <option key={mode.id} value={mode.id}>{mode.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider block">
                Horário de Disparo
              </Label>
              <label className="flex items-center gap-2 cursor-pointer mb-3">
                <input
                  type="checkbox"
                  checked={formData.auto_hour}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    auto_hour: e.target.checked 
                  }))}
                  className="rounded border-input h-4 w-4"
                />
                <span className={`text-sm font-medium ${formData.auto_hour ? 'text-[hsl(var(--accent-600))]' : 'text-muted-foreground'}`}>
                  Automático (IA)
                </span>
              </label>
              <Input
                type="time"
                value={formData.hour}
                onChange={(e) => setFormData(prev => ({ ...prev, hour: e.target.value }))}
                disabled={formData.auto_hour}
                className={`h-10 ${formData.auto_hour ? 'opacity-40 bg-muted' : ''}`}
              />
            </div>
          </div>

          {/* Content Editor for channels that have content */}
          {showContentEditor && (
            <div className="space-y-2">
              {/* Email Subject */}
              {formData.channel === 'email' && (
                <div className="space-y-2 mb-4">
                  <Label htmlFor="subject">Assunto do E-mail</Label>
                  <Input
                    id="subject"
                    value={getCurrentSubject()}
                    onChange={(e) => setCurrentSubject(e.target.value)}
                    placeholder="Ex: Lembrete de vencimento - {valor}"
                  />
                </div>
              )}

              <div className="flex items-center justify-between">
                <Label>Conteúdo da Mensagem</Label>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={enhanceWithAI}
                    disabled={isEnhancing}
                    className="gap-2 border-[hsl(var(--accent-300))] text-[hsl(var(--accent-600))] hover:bg-[hsl(var(--accent-50))]"
                  >
                    {isEnhancing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Wand2 className="h-4 w-4" />
                    )}
                    Melhorar com IA
                  </Button>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPreview(!showPreview)}
                          className="gap-2"
                        >
                          {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          {showPreview ? 'Ocultar' : 'Pré-visualizar'}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {showPreview ? 'Ocultar pré-visualização' : 'Ver pré-visualização'}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>

              {formData.channel === 'email' ? (
                <RichTextEditor
                  content={getCurrentContent()}
                  onChange={(content) => setCurrentContent(content)}
                  placeholder="Olá {nome}, identificamos que seu boleto de {valor} vence em..."
                />
              ) : (
                <textarea
                  rows={6}
                  value={getCurrentContent()}
                  onChange={(e) => setCurrentContent(e.target.value)}
                  placeholder="Olá {nome}, sua fatura de {valor} vence em {vencimento}. Pague agora: {link_pagamento}"
                  className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              )}

              {/* Placeholders */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">Variáveis:</span>
                {placeholders.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => insertPlaceholder(p.key)}
                    className="px-2 py-1 text-xs rounded border bg-muted/50 hover:bg-muted font-mono transition-colors"
                    title={p.desc}
                  >
                    {p.key}
                  </button>
                ))}
              </div>

              {/* Preview */}
              {showPreview && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-medium text-muted-foreground mb-3">Pré-visualização</p>
                  {formData.channel === 'whatsapp' && (
                    <WhatsAppPreview content={channelContent.whatsapp} />
                  )}
                  {formData.channel === 'email' && (
                    <EmailPreview subject={channelContent.email.subject} content={channelContent.email.content} />
                  )}
                  {formData.channel === 'sms' && (
                    <SMSPreview content={channelContent.sms} />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Info for protesto/negativacao */}
          {!showContentEditor && (
            <div className="p-4 rounded-lg bg-muted/50 border">
              <p className="text-sm text-muted-foreground">
                {formData.channel === 'protesto' && (
                  <>
                    <AlertTriangle className="inline h-4 w-4 mr-2 text-destructive" />
                    O protesto será registrado automaticamente no cartório competente após o período configurado.
                  </>
                )}
                {formData.channel === 'negativacao' && (
                  <>
                    <XCircle className="inline h-4 w-4 mr-2 text-destructive" />
                    A negativação será enviada aos órgãos de proteção ao crédito (SPC/Serasa) após o período configurado.
                  </>
                )}
              </p>
            </div>
          )}

          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-between gap-3 pt-4">
            <div>
              {step && (
                <Button 
                  variant="ghost"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-2"
                  onClick={() => deleteMutation.mutate()}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 className="h-4 w-4" />
                  Apagar
                </Button>
              )}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={() => mutation.mutate()}
                disabled={mutation.isPending}
                className="bg-[hsl(var(--accent-500))] hover:bg-[hsl(var(--accent-600))]"
              >
                {mutation.isPending ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
